<template>
  <div>
    <div class="inputWithLabel" :class="{ isPc: !isSp }">
      <InputLabel
        v-if="!isVerifyCodeInPc"
        class="labelArea"
        :class="{ isPc: !isSp, isHasLabel: !isSp && (required || optional), styleLabelWide: styleLabelWide }"
        :required="required"
        :optional="optional"
        :right-space="rightSpace"
      >
        {{ areaLabel }}
      </InputLabel>
      <InputItem
        v-model="handleInputValue"
        :input-type="inputType"
        :placeholder-value="placeholder"
        class="inputItem"
        :disabled="disabled"
        :maxlength="maxlength"
        :class="{ hasError: hasError, isPc: !isSp, telNo: areaLabel === '電話番号', isLoginPage: isLoginPage }"
        @input="onInput"
        @blur="onBlur"
        @focus="onFocus($event)"
      />
    </div>
    <div
      class="underInputArea"
      :class="{
        isPc: !isSp && !isVerifyCodeInPc,
        isNoLabel: !isSp && !(required || optional) && !isVerifyCodeInPc,
        styleLabelWide: styleLabelWide,
      }"
    >
      <PartialErrorMessage :is-sp-flg="true" :error="displayError" class="partialErrorComponent" />
      <CaptionText v-if="captionText" class="captionText" :class="{ hasError: hasError, isPc: !isSp }">
        {{ captionText }}
      </CaptionText>
    </div>
  </div>
</template>

<script>
import InputLabel from '../Text/InputLabel.vue';
import InputItem from './InputItem.vue';
import PartialErrorMessage from '../PartialErrorMessage.vue';
import CaptionText from '../Text/CaptionText.vue';
import {
  isDependentCharacter,
  isEmailFormat,
  isFullWidth,
  isAllNumber,
  checkWordCount,
  isUserNameFormat,
  checkCharCode,
  checkAddressChar,
  checkUserId,
  isEmpty,
  isQuipperEmailFormat,
} from '../../../../helper/_validationCheck';
import {
  GENERAL_VALIDATION_MESSAGE,
  SPECIFY_VALIDATION_MESSAGE,
  generateWordCountError,
} from '../../../../helper/_validationCheckMessages';

export const VALIDATION_LIST = {
  EMPTY: 'empty',
  FULL_WIDTH: 'fullWidth',
  DEPENDENT_CHARACTER: 'dependentCharacter',
  ONLY_S_JIS: 'onlySJis',
  EMAIL: 'email',
  ADDRESS: 'address',
  TEL: 'tel',
  USER_NAME: 'username',
  ONLY_NUMBER: 'onlyNumber',
  USER_NAME_AND_EMAIL: 'userNameAndEmail',
  QUIPPER_EMAIL: 'quipperEmail',
  RESET_PASSWORD_EMAIL: 'resetPasswordEmail',
};

export const MIN_MAX_LIST = {
  TEL: {
    MIN: 9,
    MAX: 14,
  },
  USER_NAME: {
    MIN: 1,
    MAX: 40,
  },
  EMAIL: {
    MIN: 1,
    MAX: 101,
    ACCOUNT: {
      MIN: 1,
      MAX: 50,
    },
    DOMAIN: {
      MIN: 1,
      MAX: 50,
    },
  },
  QUIPPER_EMAIL: {
    MIN: 3,
    MAX: 161,
  },
};

export default {
  name: 'InputAreaWithValidation',
  components: {
    InputLabel,
    InputItem,
    PartialErrorMessage,
    CaptionText,
  },
  model: {
    prop: 'inputValue',
    event: 'change',
  },
  props: {
    areaLabel: {
      type: String,
      required: true,
    },
    inputType: {
      default: 'text',
      validator(value) {
        return ['text', 'password'].indexOf(value) !== -1;
      },
    },
    validationList: {
      type: Array,
      required: false,
      default: () => [],
      validator(valueList) {
        let validationCheckFailedFlg = true;
        valueList.forEach((value) => {
          if (Object.values(VALIDATION_LIST).indexOf(value) === -1) {
            validationCheckFailedFlg = false;
          }
        });
        return validationCheckFailedFlg;
      },
    },
    placeholder: {
      type: String,
      default: '',
    },
    inputValue: {
      type: String,
      required: true,
    },
    /**
     * 外部からのエラー
     * このコンポーネントに定義しないような独自のバリデーションを持つ場合、
     * 親コンポーネントでv-modelの値をwatchとかしてバリデーションチェックを行い、
     * errorFromOuterにpropsで渡すと、最後の項目としてチェック入る。
     * 空文字・undefinedを渡すことで『エラー無し』扱いになる。
     */
    errorFromOuter: {
      type: String,
      required: false,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    optional: {
      type: Boolean,
      required: false,
      default: false,
    },
    captionText: {
      type: String,
      required: false,
      default: undefined,
    },
    min: {
      type: Number,
      required: false,
      default: undefined,
    },
    max: {
      type: Number,
      required: false,
      default: undefined,
    },
    isSpFlg: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    rightSpace: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    styleLabelWide: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    maxlength: {
      type: String,
      required: false,
      default: '',
    },
    confirmedValue: {
      type: String || null,
      required: false,
      default: undefined,
    },
    isLoginPage: {
      type: Boolean,
      required: false,
      default: false,
    },
    validateAtMount: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      validationError: undefined,
      focusFlg: false,
      // 【IE対応】IEでのみ、propsの更新タイミングが『すべて終わった後』でリアクティブにならないので
      onInputValue: this.inputValue, // IE不要になったらinputValueに統一してください
    };
  },
  computed: {
    isSp() {
      if (typeof this.isSpFlg === 'boolean') return this.isSpFlg;
      return this.$isSp;
    },
    isVerifyCodeInPc() {
      // 認証コードはPCではラベル無しなので、これがtrueならばラベル非表示
      return !this.isSp && this.areaLabel === '認証コード';
    },
    isInputTypeText() {
      return this.inputType === 'text';
    },
    displayError() {
      if (this.validationError) return this.validationError;
      return this.errorFromOuter;
    },
    hasError() {
      // displayError自体がundefinedならエラーない
      if (!this.displayError) return false;
      // displayError自体がlengthもってるならエラーあり
      if (this.displayError.length) return true;
      return false;
    },
    handleInputValue: {
      get() {
        return this.inputValue;
      },
      set(value) {
        this.$emit('change', value);
      },
    },
  },
  watch: {
    async inputValue() {
      this.onInputValue = this.inputValue;
      if (!this.focusFlg) {
        this.validationError = await this.generateValidationCheckMessage();
      }
    },
  },
  mounted() {
    if (this.validateAtMount) {
      this.onBlur();
    }
  },
  methods: {
    onFocus(event) {
      this.focusFlg = true;
      this.$emit('focus', event);
    },
    async generateValidationCheckMessage() {
      if (!this.validationList.length) return undefined;
      if (this.confirmedValue !== undefined && this.onInputValue === this.confirmedValue) return undefined;
      if (this.onInputValue.length === 0 && this.optional) {
        // optionalを空白チェックすることはないと思うのでここで弾く
        return undefined;
      }
      if (this.validationList.includes(VALIDATION_LIST.EMPTY)) {
        // 空白チェック
        if (isEmpty(this.onInputValue)) {
          return this.areaLabel + GENERAL_VALIDATION_MESSAGE.EMPTY;
        }
      }
      if (this.min && this.max) {
        // 最大最小両方チェック
        if (!checkWordCount(this.onInputValue, min, max)) {
          return generateWordCountError(this.areaLabel, this.min, this.max, false);
        }
      }
      if (this.max !== undefined) {
        // 最大文字数だけチェック
        if (!checkWordCount(this.onInputValue, 1, this.max)) {
          return generateWordCountError(this.areaLabel, undefined, this.max, false);
        }
      }
      if (this.min !== undefined) {
        // 最小文字数だけチェック
        if (!checkWordCount(this.onInputValue, this.min, Number.MAX_SAFE_INTEGER)) {
          return generateWordCountError(this.areaLabel, this.min, undefined, false);
        }
      }
      if (this.validationList.includes(VALIDATION_LIST.USER_NAME_AND_EMAIL)) {
        // ログイン時のユーザー名・メールアドレス固有バリデーション
        // 全角チェック
        if (isFullWidth(this.onInputValue)) {
          return this.areaLabel + GENERAL_VALIDATION_MESSAGE.ONLY_HALF_WIDTH;
        }
        // 機種依存文字チェック
        if (isDependentCharacter(this.onInputValue)) {
          return this.areaLabel + GENERAL_VALIDATION_MESSAGE.ONLY_HALF_WIDTH;
        }
      }

      if (this.validationList.includes(VALIDATION_LIST.RESET_PASSWORD_EMAIL)) {
        // メール固有バリデーション
        // 文字数チェック
        // 最小文字数はエラー文言フォーマットで表現されないので、フォーマット違反に寄せる意図で0を設定
        if (!checkWordCount(this.onInputValue, 0, MIN_MAX_LIST.QUIPPER_EMAIL.MAX)) {
          return generateWordCountError(this.areaLabel, undefined, MIN_MAX_LIST.QUIPPER_EMAIL.MAX, false);
        }

        // 最小文字数違反用
        if (!checkWordCount(this.onInputValue, MIN_MAX_LIST.QUIPPER_EMAIL.MIN, MIN_MAX_LIST.QUIPPER_EMAIL.MAX)) {
          return `${SPECIFY_VALIDATION_MESSAGE.EMAIL.VALIDATION_ERROR}`;
        }

        // フォーマット違反の場合はVALIDATION_ERROR
        if (!isEmailFormat(this.onInputValue)) {
          return SPECIFY_VALIDATION_MESSAGE.EMAIL.VALIDATION_ERROR;
        }
      }

      if (this.validationList.includes(VALIDATION_LIST.EMAIL)) {
        // メール固有バリデーション
        // 文字数チェック
        if (!checkWordCount(this.onInputValue, MIN_MAX_LIST.EMAIL.MIN, MIN_MAX_LIST.EMAIL.MAX)) {
          return generateWordCountError(this.areaLabel, undefined, MIN_MAX_LIST.EMAIL.MAX, false);
        }

        // 50文字以上 or ドメインが50文字以上 or フォーマット違反の場合はVALIDATION_ERROR
        if (
          !checkWordCount(
            this.onInputValue.split('@')[0],
            MIN_MAX_LIST.EMAIL.ACCOUNT.MIN,
            MIN_MAX_LIST.EMAIL.ACCOUNT.MAX,
          ) ||
          !checkWordCount(
            this.onInputValue.split('@')[1],
            MIN_MAX_LIST.EMAIL.DOMAIN.MIN,
            MIN_MAX_LIST.EMAIL.DOMAIN.MAX,
          ) ||
          !isEmailFormat(this.onInputValue)
        ) {
          return SPECIFY_VALIDATION_MESSAGE.EMAIL.VALIDATION_ERROR;
        }
      }
      if (this.validationList.includes(VALIDATION_LIST.QUIPPER_EMAIL)) {
        // メール固有バリデーション
        // 文字数チェック
        // 最小文字数はエラー文言フォーマットで表現されないので、フォーマット違反に寄せる意図で0を設定
        if (!checkWordCount(this.onInputValue, 0, MIN_MAX_LIST.QUIPPER_EMAIL.MAX)) {
          return generateWordCountError(this.areaLabel, undefined, MIN_MAX_LIST.QUIPPER_EMAIL.MAX, false);
        }

        // フォーマット違反の場合はVALIDATION_ERROR
        if (!isQuipperEmailFormat(this.onInputValue)) {
          return SPECIFY_VALIDATION_MESSAGE.EMAIL.VALIDATION_ERROR;
        }
      }
      if (this.validationList.includes(VALIDATION_LIST.TEL)) {
        // 電話番号固有バリデーション
        if (!checkWordCount(this.onInputValue, MIN_MAX_LIST.TEL.MIN, MIN_MAX_LIST.TEL.MAX)) {
          return generateWordCountError(this.areaLabel, MIN_MAX_LIST.TEL.MIN, MIN_MAX_LIST.TEL.MAX, true);
        }
        if (!isAllNumber(this.onInputValue)) {
          return this.areaLabel + GENERAL_VALIDATION_MESSAGE.ONLY_HALF_WIDTH_NUMBER;
        }
      }
      if (this.validationList.includes(VALIDATION_LIST.USER_NAME)) {
        // ユーザー名固有バリデーション
        if (!isUserNameFormat(this.onInputValue)) {
          return SPECIFY_VALIDATION_MESSAGE.USER_NAME.VALIDATION_ERROR;
        }
        if (isAllNumber(this.onInputValue) && checkWordCount(this.onInputValue, 9, 10)) {
          return SPECIFY_VALIDATION_MESSAGE.USER_NAME.ALL_NUM_ERROR;
        }
        if (!checkWordCount(this.onInputValue, MIN_MAX_LIST.USER_NAME.MIN, MIN_MAX_LIST.USER_NAME.MAX)) {
          return generateWordCountError(this.areaLabel, undefined, MIN_MAX_LIST.USER_NAME.MAX);
        }
        const data = await checkUserId(this.onInputValue, this.$isSp);
        if (data === 1) return SPECIFY_VALIDATION_MESSAGE.USER_NAME.DUPLICATE_USER_ID;
      }
      if (this.validationList.includes(VALIDATION_LIST.FULL_WIDTH)) {
        // 全角チェック
        if (isFullWidth(this.onInputValue)) {
          return this.areaLabel + GENERAL_VALIDATION_MESSAGE.FULL_WIDTH;
        }
      }
      if (this.validationList.includes(VALIDATION_LIST.DEPENDENT_CHARACTER)) {
        // 機種依存文字チェック
        if (isDependentCharacter(this.onInputValue)) {
          return this.areaLabel + GENERAL_VALIDATION_MESSAGE.DEPENDENT_CHARACTER;
        }
      }
      if (this.validationList.includes(VALIDATION_LIST.ONLY_HALF_WIDTH_NUMBER)) {
        // 数字のみチェック
        if (!isAllNumber(this.onInputValue)) {
          return this.areaLabel + GENERAL_VALIDATION_MESSAGE.ONLY_HALF_WIDTH_NUMBER;
        }
      }
      if (this.validationList.includes(VALIDATION_LIST.ONLY_S_JIS)) {
        // S JISチェック
        const data = await checkCharCode(this.onInputValue);
        if (data === 1) return this.areaLabel + GENERAL_VALIDATION_MESSAGE.INVALID_CHAR_ERROR;
      }
      if (this.validationList.includes(VALIDATION_LIST.ADDRESS)) {
        // S JISチェック
        const data = await checkAddressChar(this.onInputValue);
        if (data === 1) return this.areaLabel + GENERAL_VALIDATION_MESSAGE.INVALID_CHAR_ERROR;
      }
      return undefined;
    },
    onInput(event) {
      this.onInputValue = event.target.value;
    },
    async onBlur() {
      this.validationError = await this.generateValidationCheckMessage();
      this.$emit('watchValidation', !!this.validationError);
      this.$emit('blur');
      this.focusFlg = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'css/contents/common/global';

$label-width-pc: 120px;
$label-width-pc-wide: 124px;
$label-width-has-label-pc: 184px;

.inputWithLabel {
  .labelArea.isPc {
    width: $label-width-pc;
    margin: auto 0;
    flex-shrink: 0;

    &.styleLabelWide {
      width: $label-width-pc-wide;
      flex-shrink: 0;
    }

    &.isHasLabel {
      width: $label-width-has-label-pc;
      flex-shrink: 0;
    }
  }

  .inputItem {
    &.isPc {
      width: 432px;

      &.isLoginPage {
        width: 316px;
      }
    }

    &.telNo.isPc {
      width: 212px;
    }
  }

  &.isPc {
    display: flex;
  }
}

.underInputArea {
  &.isPc {
    margin-left: $label-width-has-label-pc;

    &.styleLabelWide {
      margin-left: $label-width-pc-wide;
    }
  }

  &.isNoLabel {
    margin-left: $label-width-pc;
  }
}

.inputItem.hasError,
.inputItem.hasError:focus {
  border: $border-alert;
}

.partialErrorComponent {
  margin-top: 8px;
}

.captionText {
  margin-top: 8px;

  &.hasError {
    margin-top: 8px;

    &.isPc {
      margin-top: 12px;
    }
  }
}
</style>
